/**
 * The World package record — ADR-033's one seam in the kernel.
 *
 * A packaged avatar is ONE roster record that grew (design note §0): World's
 * `prebuilt/manifest.json` entry — its seven fields untouched — plus the
 * shelf (`set`), the roles, the ADR-189 facets, a declared height, the rig
 * facts MEASURED at package time, the carousel's selection loop, the
 * animation overrides as ADR-189 `SetBinding`s VERBATIM with a per-avatar
 * entity path, and provenance. NEXUS reads four of these fields and ignores
 * the rest; World's resolver ingests the bindings unchanged.
 *
 * Two shapes, deliberately apart:
 *  - `PackageChoices` (document.ts) — what the AUTHOR decided, persisted in
 *    the Document (F6). Lenient while in progress: a role without an
 *    archetype, no take starred yet.
 *  - `PackageRecord` (here) — what the FILE says. Strict: composing it from
 *    choices + measured facts is the readiness gate, and every reason it
 *    refuses is returned as a LIST (a refusal must reach the eye), never the
 *    first one found.
 *
 * Nothing here is typed by a person that a measurement could supply: bone,
 * digit and tail counts come from the rig's names; declarations from the
 * Document's edits; the entity path from facets + id.
 *
 * Depends on: zod, ir/errors, session/document, session/picker-facets.
 * Depended on by: the app's
 * package door (composes and writes), the reopen census (reads back).
 */
import { z } from 'zod';
import { KernelError, SchemaValidationError } from '../ir/errors.js';
import {
  clipIdSchema,
  facetBodySchema,
  facetPresentationSchema,
  packageRoleSchema,
  selectorPathSchema,
  setBindingSchema,
  slugSchema,
  type AnimatorDocument,
  type PackageChoices,
  type PackageFacets,
} from './document.js';
import { resolvePickerFacets } from './picker-facets.js';

export const PACKAGE_RECORD_SCHEMA_VERSION = '1.0.0';

/** The declarations the export door can write into `extras.poqpoq.*` today. */
export const packageDeclarationSchema = z.enum(['headSeating', 'armSpace']);
export type PackageDeclaration = z.infer<typeof packageDeclarationSchema>;

/** Rig facts, measured never typed (design note §3.1). */
export const packageRigSchema = z.strictObject({
  /** World's family id (the ADR-178 library the clips come from), e.g. `tripo-2026-handed`. */
  family: z.string().min(1),
  bones: z.number().int().positive(),
  digits: z.strictObject({ left: z.number().int().nonnegative(), right: z.number().int().nonnegative() }),
  tail: z.number().int().nonnegative(),
  declarations: z.array(packageDeclarationSchema),
});

export const packageNpcSchema = z.strictObject({
  /** World's `npcEntityPath` vocabulary (`npc/humanoid/civilian`, `npc/creature`, …). */
  archetype: selectorPathSchema,
  note: z.string().optional(),
});

export const packageProvenanceSchema = z.strictObject({
  harry: z.string().min(1),
  document: z.string().min(1),
  glbSha256: z.string().regex(/^[0-9a-f]{64}$/),
  packagedAt: z.iso.datetime(),
});

/** The height band a declared avatar may claim, in metres — World's own reader ignores anything outside 0.3–6 (PR #1626). */
export const HEIGHT_METERS_BAND = { min: 0.3, max: 6 } as const;

export const packageRecordSchema = z
  .strictObject({
    schemaVersion: z.literal(PACKAGE_RECORD_SCHEMA_VERSION),
    // World's seven, untouched (prebuiltManifest.ts).
    id: slugSchema,
    name: z.string().min(1),
    file: z.string().regex(/^[^/\\]+\.glb$/),
    thumbnail: z
      .string()
      .regex(/^[^/\\]+\.(webp|png)$/)
      .nullable(),
    format: z.literal('glb'),
    artist: z.string().min(1),
    tags: z.array(z.string().min(1)),
    // The growth.
    set: slugSchema,
    roles: z.array(packageRoleSchema).min(1),
    facets: z.strictObject({ body: facetBodySchema, presentation: facetPresentationSchema }),
    heightMeters: z.number().gt(HEIGHT_METERS_BAND.min).lte(HEIGHT_METERS_BAND.max),
    rig: packageRigSchema,
    presentation: z.strictObject({ selectionClip: clipIdSchema }),
    animation: z.strictObject({ entityPath: selectorPathSchema, bindings: z.array(setBindingSchema) }),
    npc: packageNpcSchema.optional(),
    provenance: packageProvenanceSchema,
  })
  .superRefine((r, ctx) => {
    if (new Set(r.roles).size !== r.roles.length) {
      ctx.addIssue({ code: 'custom', path: ['roles'], message: 'a role appears twice' });
    }
    const npcRole = r.roles.includes('npc');
    if (npcRole && r.npc === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['npc'],
        message: 'the npc role needs an archetype — World cannot spawn "an NPC" without knowing which set it joins',
      });
    }
    if (!npcRole && r.npc !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['npc'], message: 'an npc block without the npc role is a contradiction' });
    }
    // A package may only bind ITSELF. A broader selector would edit the
    // family's defaults from inside one avatar's file (F3: facet defaults
    // live in sets.json, exceptions in the package). Two halves: the entity
    // path must be the one THIS record's facets + id derive (or a file could
    // declare `mc/humanoid` and every binding "match" it — Codex P1, #144),
    // and every binding's selector must equal that path.
    const ownPath = entityPathFor(r.facets, r.id);
    if (r.animation.entityPath !== ownPath) {
      ctx.addIssue({
        code: 'custom',
        path: ['animation', 'entityPath'],
        message: `entity path "${r.animation.entityPath}" is not this avatar's own "${ownPath}" — a package binds itself, never another path`,
      });
    }
    const seen = new Set<string>();
    r.animation.bindings.forEach((b, i) => {
      if (b.selector !== r.animation.entityPath) {
        ctx.addIssue({
          code: 'custom',
          path: ['animation', 'bindings', i, 'selector'],
          message: `selector "${b.selector}" is not this avatar's entity path "${r.animation.entityPath}"`,
        });
      }
      const tuple = `${b.key}@${b.state ?? 'neutral'}`;
      if (seen.has(tuple)) {
        ctx.addIssue({
          code: 'custom',
          path: ['animation', 'bindings', i],
          message: `duplicate binding for ${tuple} — one key, one pool per state (ADR-189)`,
        });
      }
      seen.add(tuple);
    });
    if (r.file !== `${r.id}.glb`) {
      ctx.addIssue({
        code: 'custom',
        path: ['file'],
        message: `file must be "${r.id}.glb" — the id is the roster key and the filename`,
      });
    }
  });
export type PackageRecord = z.infer<typeof packageRecordSchema>;

/** Validate raw data into a record, loudly (the parse* idiom). */
export function parsePackageRecord(raw: unknown): PackageRecord {
  const parsed = packageRecordSchema.safeParse(raw);
  if (!parsed.success) {
    throw new SchemaValidationError(
      'PackageRecord',
      parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
    );
  }
  return parsed.data;
}

/**
 * A refusal that carries EVERY reason, so the panel can list them and the
 * Package button's disabled state says the same thing (design note §4).
 */
export class PackageRefusal extends KernelError {
  constructor(readonly reasons: readonly string[]) {
    super(`package refused (${reasons.length} reason(s)):\n` + reasons.map((r) => `  - ${r}`).join('\n'));
  }
}

/**
 * A roster id from a display name: lowercase, runs of anything that is not
 * a letter or digit become one hyphen. Refuses a name with nothing to keep.
 */
export function slugify(name: string): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (slug === '') throw new PackageRefusal([`"${name}" leaves no letters or digits to make an id from`]);
  return slug;
}

/**
 * The per-avatar entity path: `mc/<body>/<presentation>/<id>`. World's
 * player root is `mc/humanoid` (AnimationSetResolver.ts:62), so the `human`
 * facet maps onto THAT token rather than minting a synonym; the other body
 * tokens are ADR-189's own. Deepest selector wins, so this path sits under
 * every facet-level default and beats it.
 */
export function entityPathFor(facets: PackageFacets, id: string): string {
  const body = facets.body === 'human' ? 'humanoid' : facets.body;
  return `mc/${body}/${facets.presentation}/${id}`;
}

export interface RigFacts {
  readonly bones: number;
  readonly digits: { readonly left: number; readonly right: number };
  readonly tail: number;
}

const DIGIT = /^(Left|Right)Hand(Thumb|Index|Middle|Ring|Pinky)\d+$/;
const TAIL = /^Tail\d+$/;

/**
 * Bone, digit and tail counts from the rig's names. Session names may still
 * carry the `_graft` suffix both export doors strip; it is stripped here too,
 * so the facts describe the FILE. Digits are counted per side as distinct
 * chains (a five-digit hand has five names at index 1), never assumed
 * symmetric — a one-armed character is a character.
 */
export function rigFactsFromBoneNames(names: readonly string[]): RigFacts {
  const canonical = names.map((n) => n.replace(/_graft$/, ''));
  const digits = { left: new Set<string>(), right: new Set<string>() };
  let tail = 0;
  for (const n of canonical) {
    const m = DIGIT.exec(n);
    if (m !== null) digits[m[1] === 'Left' ? 'left' : 'right'].add(m[2]!);
    else if (TAIL.test(n)) tail += 1;
  }
  return { bones: canonical.length, digits: { left: digits.left.size, right: digits.right.size }, tail };
}

/** What the Document has decided that the export door writes into `extras.poqpoq.*`. */
export function declarationsOf(doc: AnimatorDocument): PackageDeclaration[] {
  const out: PackageDeclaration[] = [];
  if (doc.edits.headSpace !== undefined) out.push('headSeating');
  if (doc.edits.armSpace !== undefined) out.push('armSpace');
  return out;
}

export interface ComposePackageInputs {
  readonly doc: AnimatorDocument;
  /** Defaults to `doc.package`; absent = nothing was ever decided. */
  readonly choices?: PackageChoices;
  /** World's family id this rig matched (the library read, phase 3) — null = no match. */
  readonly family: string | null;
  /** The session rig's names (phase 1) — superseded by `census` when the file has been written. */
  readonly boneNames: readonly string[];
  /**
   * Facts read from the WRITTEN file (phase 2, `packageCensus`): bones, digits,
   * tail and the declarations actually present. When given, the record
   * describes the bytes, never the session's belief about them.
   */
  readonly census?: { readonly facts: RigFacts; readonly declarations: readonly PackageDeclaration[] };
  /** The shelf this release ships to, e.g. `enhanced-2026`. */
  readonly set: string;
  /**
   * The roster row this package REPLACES (identity's `replaces`), for its
   * picker-facet tags; absent/null = a new row, or no roster read — World's
   * ingest then carries the same way (picker-facets.ts).
   */
  readonly replaces?: { readonly tags: readonly string[] } | null;
  readonly provenance: { readonly harry: string; readonly glbSha256: string; readonly packagedAt: string };
}

/**
 * Choices + measurements → the record, or a PackageRefusal listing every
 * reason at once. This IS the readiness gate: the panel renders the same
 * list beside its pills, and the Package button is disabled by it.
 */
export function composePackageRecord(inputs: ComposePackageInputs): PackageRecord {
  const { doc, family, set, provenance } = inputs;
  const choices = inputs.choices ?? doc.package;
  if (choices === undefined) throw new PackageRefusal(['nothing decided yet — no roles, facets or height']);

  const reasons: string[] = [];
  let id = '';
  try {
    id = choices.id ?? slugify(choices.name ?? doc.name);
  } catch (e) {
    reasons.push(e instanceof PackageRefusal ? e.reasons[0]! : String(e));
  }
  if (family === null) reasons.push('the rig matches no World animation family — World could not animate it');
  if (choices.selectionClip === undefined) reasons.push('no selection loop chosen — the carousel has nothing to play');
  if (choices.heroFavourite === undefined) reasons.push(choices.heroTakes.length === 0 ? 'no hero take yet' : 'no hero take starred');
  if (choices.roles.includes('npc') && choices.npc === undefined) reasons.push('the npc role needs an archetype');
  const h = choices.heightMeters.value;
  if (!(h > HEIGHT_METERS_BAND.min && h <= HEIGHT_METERS_BAND.max)) {
    reasons.push(`height ${h} m is outside (${HEIGHT_METERS_BAND.min}, ${HEIGHT_METERS_BAND.max}]`);
  }
  if (choices.artist === undefined) reasons.push('no artist named');
  const picker = resolvePickerFacets(choices, inputs.replaces?.tags ?? null);
  reasons.push(...picker.refusals);
  if (reasons.length > 0) throw new PackageRefusal(reasons);

  const facts = inputs.census?.facts ?? rigFactsFromBoneNames(inputs.boneNames);
  const declarations = inputs.census?.declarations ?? declarationsOf(doc);
  const entityPath = entityPathFor(choices.facets, id);
  const raw = {
    schemaVersion: PACKAGE_RECORD_SCHEMA_VERSION,
    id,
    name: choices.name ?? doc.name,
    file: `${id}.glb`,
    thumbnail: `${id}.png`,
    format: 'glb',
    artist: choices.artist,
    // The author's tags + the picker facets expanded, carried over per prefix
    // from the replaced row when the author left one unset (picker-facets.ts).
    tags: [...picker.tags],
    set,
    roles: [...choices.roles],
    facets: { ...choices.facets },
    heightMeters: h,
    rig: { family, ...facts, declarations: [...declarations] },
    presentation: { selectionClip: choices.selectionClip },
    animation: {
      entityPath,
      bindings: choices.pins.map((p) => ({
        key: p.key,
        selector: entityPath,
        ...(p.state !== undefined ? { state: p.state } : {}),
        clips: [...p.clips],
        ...(p.draw !== undefined ? { draw: { ...p.draw } } : {}),
        ...(p.notes !== undefined ? { notes: p.notes } : {}),
      })),
    },
    ...(choices.npc !== undefined && choices.roles.includes('npc') ? { npc: { ...choices.npc } } : {}),
    provenance: { ...provenance, document: `${doc.name}.harry.json` },
  };
  try {
    return parsePackageRecord(raw);
  } catch (e) {
    if (e instanceof SchemaValidationError) throw new PackageRefusal(e.issues);
    throw e;
  }
}
